/**
 * Squeezes Block 1's non-sung overhead before any sung line is deleted.
 *
 * Block 1 = [arrangement_cues] + [Character_Dossier] + tag lines + sung text. The model writes the
 * sung text long and adds the rest around it, so about 13 in 15 real runs landed over the 4,920
 * ceiling. The old last resort (trimLyricsBodyToHardCap) then deleted sung lines from the END of the
 * song, which is the final chorus and the outro. Cue detail and dossier prose are the cheapest things
 * to lose, so they are shortened first, line by line at clause boundaries; whatever is still over is
 * left to the sung-line trim.
 *
 * Floors keep the squeeze from gutting what matters: a cue line never drops below about 110 characters
 * (the layer schedule needs its substance), a dossier line never below 90.
 */

const CUES_RE = /\[arrangement_cues[\s\S]*?\n\]/;
const DOSSIER_RE = /\[Character_Dossier\][\s\S]*?(?=\n\s*\n|\n\/\/\/)/;

const CUES_BUDGET = 800;
const CUE_FLOOR = 110;
const CUE_KEY_FLOOR = 180;
const DOSSIER_FLOOR = 90;

/** A cue line that carries no information of its own ("Dynamic arc per section annotated below"). */
const NO_INFO_LINE = /^\s*-?\s*[^:]*\b(annotated|noted|marked|shown|see)\b[^:]*\bbelow\b[^:]*$/i;

/** Cue lines that carry the BEHAVIOR schedule keep more of themselves than the rest. */
const KEY_CUE_LINE = /\b(schedule|climax|behavior|ending|outro)\b/i;

const TRAILING = /[\s,;:—\-→|(+]+$/;

/** Cuts a line at the last clause boundary before `max`, never mid-word. */
export function clipAtClause(line: string, max: number): string {
  if (line.length <= max) return line;
  const slice = line.slice(0, max);
  for (const sep of ['; ', ', ', ' — ', ' | ', ' ']) {
    const i = slice.lastIndexOf(sep);
    if (i >= max * 0.55) return slice.slice(0, i).replace(TRAILING, '');
  }
  return slice.replace(TRAILING, '');
}

/** Clips the lines in [from, to) of a block's line list; lines matching `keep.re` are never clipped below `keep.min`. */
function clipRange(lines: string[], from: number, to: number, perLine: number, keep?: { re: RegExp; min: number }): string[] {
  return lines.map((l, i) => {
    if (i < from || i >= to) return l;
    const max = keep && keep.re.test(l) ? Math.max(perLine, keep.min) : perLine;
    return clipAtClause(l, max);
  });
}

export interface SqueezeResult {
  body: string;
  /** characters removed from the cue block and from the dossier */
  saved: { cues: number; dossier: number };
}

/** Replaces `from` with `to` without treating `$` in `to` as a replacement pattern. */
const swap = (text: string, from: string, to: string): string => text.replace(from, () => to);

/**
 * If `body` is over `absMax`, shortens the cue block and then the dossier, each in steps and only as far
 * as needed to reach `targetMax`. Returns the body unchanged when it already fits.
 */
export function squeezeBlock1Overhead(body: string, absMax: number, targetMax: number): SqueezeResult {
  const saved = { cues: 0, dossier: 0 };
  if (body.length <= absMax) return { body, saved };

  let out = body;
  const cues = body.match(CUES_RE)?.[0];
  if (cues) {
    // Cue lines are everything between the opening "[arrangement_cues |" line and the closing "]".
    const all = cues.split('\n').filter((l, i, a) => i === 0 || i === a.length - 1 || !NO_INFO_LINE.test(l));
    const inner = all.length - 2;
    const floor = Math.max(CUE_FLOOR, Math.floor(CUES_BUDGET / Math.max(1, inner)));
    let shorter = all.join('\n');
    out = swap(body, cues, shorter);
    for (let per = 240; per >= floor && out.length > targetMax; per -= 20) {
      shorter = clipRange(all, 1, all.length - 1, per, { re: KEY_CUE_LINE, min: CUE_KEY_FLOOR }).join('\n');
      out = swap(body, cues, shorter);
    }
    saved.cues = cues.length - shorter.length;
  }

  const afterCues = out;
  const dossier = afterCues.match(DOSSIER_RE)?.[0];
  if (dossier && afterCues.length > targetMax) {
    const lines = dossier.split('\n');
    let shorter = dossier;
    for (let per = 140; per >= DOSSIER_FLOOR && out.length > targetMax; per -= 10) {
      shorter = clipRange(lines, 1, lines.length, per).join('\n');
      out = swap(afterCues, dossier, shorter);
    }
    saved.dossier = dossier.length - shorter.length;
  }
  return { body: out, saved };
}
